<template>
    <ListFrame
        class="assets-overview"
        :loading="initialAssetsLoading"
        :no-results="!resultExists"
        :error="error"
        :total="assets?.total"
    >
        <template #filter="{ isMobile }">
            <!-- @vue-ignore -->
            <component
                v-model:selected="selected"
                @update:selected="refetchList"
                @on-sort-selection-changed="onChangeSortOrder"
                @on-open-filter="onOpenFilterHandler"
                v-bind="listFilterComponentProps"
                :is="listFilterComponent"
                :is-mobile-view="isMobile"
                :sort-save-key="isSearch ? 'asset_search_sort' : 'asset_sort'"
                :initial-selected-sort-entry="sorting"
            />
        </template>
        <template #content>
            <InfinityScroll
                :reach-bottom-text="$t('product-and-asset.Loading {assetsName}', { assetsName: assetName })"
                @load-more="onLoadMore"
                :reset-key="infiniteResetKey"
            >
                <TilesGrid
                    :tiles="listCards"
                    always-single-col
                    @select="emitSelect"
                    class="assets-overview-tiles-grid"
                    :class="{ dense: dense }"
                />
            </InfinityScroll>
        </template>
        <template #no-results>
            <NoSearchResultsError :search-tab="assetNamePlural" :class="{ 'q-mx-lg': $q.screen.lt.sm }" />
        </template>
    </ListFrame>
</template>

<script setup lang="ts">
/* eslint-disable @typescript-eslint/no-explicit-any */ // TODO: Remove this disable and fix all any types in this file
import { computed, ref, toRefs, watch } from "vue";
import { useAssetsOverviewSort } from "./assets-overview.model";
import { loadMore } from "@/shared/components/scroll/lazy-loading-fetch";
import { Facet, FacetCollection, FacetSelected } from "@/shared/facets/facets.model";
import ListFrame from "@/shared/list-frame/list-frame.vue";
import ListFilterRenderer from "@/shared/list-filter/list-filter-renderer.vue";
import ListFilterUrlSync from "@/shared/list-filter/list-filter-renderer-url-sync.vue";
import TilesGrid from "@/shared/components/tiles/tiles-grid.vue";
import {
    QualificationLevelSelectionOptions,
    QualificationLevelSelectionResult,
} from "@/shared/environment/ability.types";
import { useAssetDataSource } from "@/abilities/product-and-asset/pages/asset-data-source";
import { SortEntry } from "@/shared/components/sort-dropdown/sort-dropdown.model";
import { useAssetNames } from "@/abilities/product-and-asset/composables/asset-names";
import { useHeaderMenuEntries } from "@/shared/search/header-menu-entries.ts";
import { CommonSearchTabAliases } from "@/shared/environment/common-route-aliases.ts";
import { useScreenSize } from "@/shared/screen/composables/screen-size.ts";
import NoSearchResultsError from "@/shared/search/no-search-results-error.vue";
import InfinityScroll from "@/shared/components/scroll/infinity-scroll.vue";
import { LAZY_LOAD_AMOUNT } from "@/shared/environment/consts.ts";
import { useMatomo } from "@/shared/analytics/matomo.ts";

const props = withDefaults(
    defineProps<{
        fullTextSearchValue?: string;
        productId?: string; //TODO validate if productId is necessary or global product param is enough
        showJumpToProduct?: boolean;
        emitSelectionInTiles?: boolean;
        multiLevel?: boolean;
        hasLevelTitle?: boolean;
        selectionData?: QualificationLevelSelectionResult;
        selectionOptions?: QualificationLevelSelectionOptions;
        dense?: boolean;
        isSearch?: boolean;
    }>(),
    {
        fullTextSearchValue: "",
        productId: "",
        showJumpToProduct: true,
        multiLevel: false,
        hasLevelTitle: false,
        dense: false,
        isSearch: false,
    }
);

const emit = defineEmits<{ select: [...params: any[]]; onOpenNeedQualification: [] }>();

const facet = ref<Facet>();
const { selectionData } = toRefs(props);
const selected = ref<FacetSelected[]>([]);
const infiniteResetKey = ref<number>(0);
const { trackSearch } = useMatomo();

const { isLargerThanSmall } = useScreenSize();
const assetName = useAssetNames("pluralLower");
const assetNamePlural = useAssetNames("plural");
const { menuEntries } = useHeaderMenuEntries(computed(() => CommonSearchTabAliases.assets));

const { sortOrderItems, sorting } = useAssetsOverviewSort(props.isSearch);

const selectedProducts = computed<string[] | undefined>(() => {
    return selectionData?.value?.product ? [selectionData.value.product] : undefined;
});

// predefined list of asset ids to select from
const assetQualificationSelectedOptions = computed<string[] | undefined>(() => {
    return props.selectionOptions?.asset;
});

const preselected = computed(() => {
    return selectedProducts.value ? { custom_products: selectedProducts.value } : undefined;
});

const listFilterComponent = computed(() => {
    return selectionData?.value ? ListFilterRenderer : ListFilterUrlSync;
});

const listFilterComponentProps = computed(() => {
    let listFilterProps = {
        fetchAggregation: fetchAggregation,
        "facet-collection-id": FacetCollection.assetSelection,
        "more-menu-entries": isLargerThanSmall.value && !selectionData.value ? [] : menuEntries.value,
        "sort-order-entries": sortOrderItems.value,
        "initial-selected-sort-entry": sorting.value,
        product: props.productId,
    };

    if (selectionData?.value)
        listFilterProps = {
            ...listFilterProps,
            // @ts-expect-error - preselect prop type mismatch with listFilterProps union
            preselect: preselected.value,
        };
    else listFilterProps = { ...listFilterProps, product: props.productId };

    return listFilterProps;
});

const emitSelect = (...params: any) => {
    emit("select", ...params);
};

const fullTextSearchValue = computed(() => {
    return props.fullTextSearchValue;
});

const resultExists = ref(false);

const { refetch, error, fetchMore, assets, listCards, fetchAggregation } = useAssetDataSource(
    computed(() => {
        return { text: fullTextSearchValue.value, product: props.productId };
    }),
    props.showJumpToProduct,
    props.emitSelectionInTiles ?? false,
    selected,
    sorting,
    facet,
    assetQualificationSelectedOptions
);

const initialAssetsLoading = computed(() => {
    return !assets.value;
});

watch(assets, () => {
    if (!assets.value?.assets) {
        return;
    }
    resultExists.value = assets.value?.assets.length > 0;
    trackSearch(fullTextSearchValue.value, "assets", assets.value?.total.count);
});

const onLoadMore = async (done: (stop: boolean) => void) => {
    if (!assets.value?.assets?.length || assets.value.assets.length === assets.value.total.count) {
        done(true);
        return;
    }

    const lastAsset = assets.value.assets[assets.value.assets.length - 1];
    if (!lastAsset.cursor) {
        console.error("No cursor found for last asset, cannot load more.", lastAsset);
        done(true);
        return;
    }
    try {
        const result = await loadMore(fetchMore, lastAsset.cursor, LAZY_LOAD_AMOUNT);
        let loadMoreLastCursor = undefined;
        if (result?.data?.assets?.assets && result.data.assets.assets.length > 0) {
            loadMoreLastCursor = result.data.assets.assets[result.data.assets.assets.length - 1].cursor;
        }

        const stop = loadMoreLastCursor ? lastAsset.cursor === loadMoreLastCursor : true;
        done(stop);
    } catch (e) {
        console.error(e);
        done(true);
    }
};

const onOpenFilterHandler = (facetProp: Facet) => {
    facet.value = facetProp;
};

// We need to count up `infiniteResetKey` after a refetch so that `InfinityScroll` (and
// internally Quasar's `QInfiniteScroll`) resets its internal loading state.
// Without this, changing filters or sort could leave the infinite scroll in a
// "done" state, and it would not call on load more anymore.
const refetchList = async () => {
    await refetch();
    infiniteResetKey.value += 1;
};

const onChangeSortOrder = async (selectedItem: SortEntry) => {
    sorting.value = { field: selectedItem.field, order: selectedItem.order };
    await refetchList();
};
</script>

<style lang="scss" scoped>
.assets-overview-tiles-grid:not(.dense) {
    padding: 0;
}
:deep(.slot-content) {
    display: grid;
}
</style>
